import { React, useRef, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.css";
import "../../styles/Report.css";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { HiChevronRight } from "react-icons/hi2";

import Wrapper from "../../components/Wrapper";
import { useDispatch, useSelector } from "react-redux";
import { fetchPublicReport, fetchReport } from "../../store/features/reportSlice";
import { selectCurrentRole } from "../../store/features/roleSlice";
import { useParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import FullReport from "../../ui/src/pages/FullReport";
import axiosInstance from "../../utils/axios";
import { getTranslation } from "../../ui/src/translations";

// MAIN FUNCTION
function Report({ isPublic }) {
  const componentRef = useRef();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { report, status, recommendation, fullResponse } = useSelector(
    (state) => state.report
  );
  const ReportWrapper = ({ children }) => {
    return isPublic ? <>{children}</> : <Wrapper>{children}</Wrapper>;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isPublic) {
      dispatch(fetchPublicReport({ id }));
    } else {
      dispatch(fetchReport({ id }));
    }
  }, [dispatch, id, isPublic]);

  // Get logged-in user's role and partner info to use as fallback
  // when the report data has no entity_name (farmer has no DB link to a partner)
  const currentRole = useSelector(selectCurrentRole);
  const { partner } = useSelector((state) => state.partner);
  const { profile: foProfile } = useSelector((state) => state.profile);

  const waterReportExist = false;
  const [selectedValue, setSelectedValue] = useState("SoilReport");
  const [language, setLanguage] = useState("english");
  const [fileName, setFileName] = useState("SoilDoctor-Report");

  const t = getTranslation(language);

  const baseReportData =
    fullResponse ||
    { report, recommData: { recommendation } };

  // When the report has no entity_name, inject the logged-in user's partner name as fallback.
  // PARTNER role  → use state.partner.partner.entity_name
  // FIELD_OFFICER → use state.profile.profile.entity_name (field officer's linked partner name)
  // Other roles   → no fallback (show Ekosight as default)
  const fullReportData = (() => {
    if (!baseReportData?.report?.entity_name && baseReportData?.report) {
      let fallbackEntityName = null;
      if ((currentRole === 'PARTNER' || currentRole === 'TEAM_MEMBER') && partner?.entity_name) {
        fallbackEntityName = partner.entity_name;
      } else if (currentRole === 'FIELD_OFFICER' && foProfile?.entity_name) {
        fallbackEntityName = foProfile.entity_name;
      }
      if (fallbackEntityName) {
        return {
          ...baseReportData,
          report: { ...baseReportData.report, entity_name: fallbackEntityName },
        };
      }
    }
    return baseReportData;
  })();

  const isUnderReview = fullReportData?.report?.report_status === 'R';

  const forceUpdateStatusToS = async () => {
    try {
      await axiosInstance.put('/super-admin/update-report-status', {
        sample_id: fullReportData.report.sample_id,
        status: 'S'
      });
      
      if (isPublic) {
        dispatch(fetchPublicReport({ id }));
      } else {
        dispatch(fetchReport({ id }));
      }
      return true;
    } catch (error) {
      console.error('Failed to update report status:', error);
      alert('Failed to override report status. Please try again.');
      return false;
    }
  };

  // Download v2: fetch backend-generated PDF and save
  const handleDownloadV2 = async () => {
    if (isUnderReview && currentRole !== "SUPPORT") {
      const proceed = window.confirm(t.warningUnderReview);
      if (!proceed) return;

      await forceUpdateStatusToS();
    }

    alert("Generating PDF... Please wait few seconds... It will be downloaded automatically.");

    try {
      const langParam = language;
      const url = `/make-pdf/${langParam}`;

      const response = await axiosInstance.post(
        url,
        fullReportData || {},
        {
          withCredentials: true,
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const objectUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${fileName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(objectUrl);

    } catch (error) {
      console.error('Failed to download PDF (v2):', error);
    }
  };

  const handleDownloadLandscape = async () => {
    if (isUnderReview  && currentRole !== "SUPPORT") {
      const proceed = window.confirm(t.warningUnderReview);
      if (!proceed) return;

      await forceUpdateStatusToS();
    }

    alert("Generating Landscape Summary... Please wait a few seconds... It will be downloaded automatically.");

    try {
      const langParam = language;
      const url = `/make-landscape-pdf/${langParam}`;

      const response = await axiosInstance.post(
        url,
        fullReportData || {},
        {
          withCredentials: true,
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const objectUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${fileName}_Landscape.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(objectUrl);

    } catch (error) {
      console.error('Failed to download Landscape PDF:', error);
    }
  };


  // Effect 2: Set Filename
  useEffect(() => {
    if (status === "succeeded" && report) {
      setFileName(
        `${report.sample_code}_${report.name}_${new Date().toLocaleDateString(
          "sv-SE"
        )}`
      );
    }
  }, [status, report]);

  // Effect 3: Collapse empty scroll space created by transform:scale on mobile.
  // Uses setProperty(..., 'important') to override the CSS `margin: 0 !important`
  // rule, and ResizeObserver to re-measure after late-rendering content changes.
  useEffect(() => {
    const el = componentRef.current;
    if (!el) return;

    const getScale = (vw) => {
      if (vw <= 360) return 0.40;
      if (vw <= 480) return 0.45;
      if (vw <= 600) return 0.55;
      if (vw <= 768) return 0.65;
      return 1;
    };

    const applyMargin = () => {
      const scale = getScale(window.innerWidth);
      if (scale < 1) {
        const h = el.scrollHeight;
        // setProperty with 'important' wins over CSS `margin: 0 !important`
        el.style.setProperty('margin-bottom', `-${Math.round(h * (1 - scale))}px`, 'important');
      } else {
        el.style.removeProperty('margin-bottom');
      }
    };

    // Use ResizeObserver so we re-measure whenever report content finishes rendering
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => requestAnimationFrame(applyMargin));
      ro.observe(el);
    }

    // Also run after next paint to catch initial layout
    requestAnimationFrame(() => requestAnimationFrame(applyMargin));

    window.addEventListener('resize', applyMargin);
    return () => {
      window.removeEventListener('resize', applyMargin);
      if (ro) ro.disconnect();
    };
  }, [status, report]); // Re-run whenever report content changes

  const handleRadioClick = (event) => {
    const { value } = event.target;
    setSelectedValue(value);

  };

  return (
    <ReportWrapper>
      {/* Select language */}
      <div className="d-flex pt-4 justify-content-end">
        <Form.Select 
          value={language} 
          onChange={(e) => setLanguage(e.target.value)}
          style={{ width: 'auto', minWidth: '150px' }}
          aria-label="Select report language"
        >
          <option value="english">English</option>
          <option value="hindi">हिंदी (Hindi)</option>
          <option value="gujarati">ગુજરાતી (Gujarati)</option>
          <option value="telugu">తెలుగు (Telugu)</option>
          <option value="punjabi">ਪੰਜਾਬੀ (Punjabi)</option>
          <option value="marathi">मराठी (Marathi)</option>
          <option value="kannada">ಕನ್ನಡ (Kannada)</option>
          <option value="tamil">தமிழ் (Tamil)</option>
          <option value="odia">ଓଡ଼ିଆ (Odia)</option>
          <option value="bengali">বাংলা (Bengali)</option>
        </Form.Select>
      </div>

      <div className="report_sections pt-2">
        {waterReportExist ? (
          <FormControl className="radio">
            <FormLabel id="demo-row-radio-buttons-group-label"></FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              defaultValue="SoilReport"
            >
              <FormControlLabel
                className="text__form"
                value="SoilReport"
                control={<Radio color="success" size="medium" />}
                label="Soil Report"
                onClick={handleRadioClick}
                checked={selectedValue === "SoilReport"}
              />
              <FormControlLabel
                className="text__form"
                value="WaterReport"
                control={<Radio color="success" size="medium" />}
                label="Water Report"
                onClick={handleRadioClick}
                checked={selectedValue === "WaterReport"}
              />
              <FormControlLabel
                className="text__form"
                value="Combined"
                control={<Radio color="success" size="medium" />}
                label="Combined Report"
                onClick={handleRadioClick}
                checked={selectedValue === "Combined"}
              />
            </RadioGroup>
          </FormControl>
        ) : null}

        <div className="paper">
          <div className="print" ref={componentRef}>
            {status === "succeeded" && report && (
              <FullReport data={fullReportData} language={language} isPublic={isPublic} />
            )}
          </div>
        </div>

          <div className="report_button d-flex flex-wrap justify-content-center gap-3 my-4">
            <button 
              type="button" 
              onClick={handleDownloadV2}
              className="btn-primary-custom"
            >
              Download Full Report (Multi-Page)
              <HiChevronRight style={{ marginLeft: "15px" }} />
            </button>

            <button 
              type="button" 
              onClick={handleDownloadLandscape}
              style={{ 
                backgroundColor: "#124B2F", 
                border: "2px solid #184B37",
                opacity: "0.95" 
              }}
            >
              Download 1 Page Report
              <HiChevronRight style={{ marginLeft: "15px" }} />
            </button>
          </div>
      </div>
    </ReportWrapper>
  );
}

export default Report;